import React, { useEffect, useState } from "react";
import {
  FaUserTie,
  FaPhoneAlt,
  FaRedo,
  FaClock,
  FaExclamationTriangle,
  FaCheckCircle,
  FaTimesCircle,
  FaUserCheck,
  FaSpinner,
} from "react-icons/fa";
import axiosInstance from "../config/axiosInstance";
import API from "../config/config";

const emptyStats = {
  assignedTotal: 0,
  callStatus: { done: 0, pending: 0 },
  callType: { firstFollowup: 0, secondFollowup: 0, thirdFollowup: 0 },
  driverStatus: {
    Pending: 0,
    Revisit: 0,
    Verified: 0,
    Rejected: 0,
    Onboarded: 0,
  },
};

const driverStatusConfig = {
  Pending: {
    label: "Pending",
    gradient: "bg-gradient-to-r from-yellow-400 to-yellow-500",
    icon: FaClock,
  },
  Onboarded: {
    label: "Onboarded",
    gradient: "bg-gradient-to-r from-blue-500 to-blue-600",
    icon: FaUserCheck,
  },
  Rejected: {
    label: "Rejected",
    gradient: "bg-gradient-to-r from-red-400 to-red-500",
    icon: FaTimesCircle,
  },
  Revisit: {
    label: "In Revisit",
    gradient: "bg-gradient-to-r from-orange-400 to-orange-500",
    icon: FaExclamationTriangle,
  },
  Verified: {
    label: "Verified",
    gradient: "bg-gradient-to-r from-emerald-400 to-emerald-500",
    icon: FaCheckCircle,
  },
};

const driverStatusOrder = ["Pending", "Onboarded", "Rejected", "Revisit", "Verified"];

const cardBaseClass =
  "text-left rounded-xl p-4 shadow-md text-white transition-transform duration-150 hover:scale-[1.02] focus:outline-none focus:ring-2 focus:ring-white focus:ring-offset-2 focus:ring-offset-transparent";
const activeRingClass =
  "ring-2 ring-white ring-offset-2 ring-offset-transparent shadow-lg";

const ActiveBadge = () => (
  <span className="absolute top-2 right-2 bg-white text-[10px] font-semibold uppercase tracking-wider text-gray-700 rounded-full px-2 py-0.5 shadow-sm">
    Filtering
  </span>
);

const AssignedEmployeeStatsPanel = ({
  assignedEmployeeId,
  assignedEmployeeName,
  activeFilter,
  onCardClick,
}) => {
  const [stats, setStats] = useState(emptyStats);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);

  useEffect(() => {
    if (!assignedEmployeeId) return;

    const controller = new AbortController();
    const fetchStats = async () => {
      setLoading(true);
      setError(null);
      try {
        const token = localStorage.getItem("adminToken");
        const response = await axiosInstance.get(
          `${API.ADMIN.DRIVER_EMPLOYEE_STATS}?assignedEmployeeId=${assignedEmployeeId}`,
          {
            headers: { Authorization: `Bearer ${token}` },
            signal: controller.signal,
          }
        );
        if (response?.data?.success && response.data.data) {
          setStats(response.data.data);
        } else {
          setStats(emptyStats);
        }
      } catch (err) {
        if (err.name === "CanceledError" || err.name === "AbortError") return;
        console.error("Error fetching employee driver stats:", err);
        setError("Failed to load stats");
        setStats(emptyStats);
      } finally {
        setLoading(false);
      }
    };

    fetchStats();
    return () => controller.abort();
  }, [assignedEmployeeId]);

  if (!assignedEmployeeId) return null;

  const isActive = (kind, value) => {
    if (kind === "reset") {
      return (
        !activeFilter?.callTypeAny &&
        !activeFilter?.initialCallStatus &&
        !activeFilter?.panelStatus
      );
    }
    return activeFilter?.[kind] === value;
  };

  const handleClick = (kind, value) => {
    if (loading) return;
    if (typeof onCardClick === "function") onCardClick(kind, value);
  };

  const renderCount = (value) =>
    loading ? (
      <FaSpinner className="animate-spin text-white text-xl" />
    ) : (
      <span>{Number(value || 0).toLocaleString()}</span>
    );

  return (
    <div className="mb-6">
      <div className="bg-white rounded-xl shadow-sm border border-gray-100 p-5">
        <div className="flex items-center justify-between mb-4">
          <div>
            <h3 className="text-base font-semibold text-gray-800">
              Assigned Employee Stats
            </h3>
            <p className="text-xs text-gray-500 mt-0.5">
              Click any card to filter the drivers list. Showing counts for{" "}
              <span className="font-medium text-gray-700">
                {assignedEmployeeName || "selected employee"}
              </span>
            </p>
          </div>
          {error && (
            <span className="text-xs text-red-500 font-medium">{error}</span>
          )}
        </div>

        {/* Row 1 — Call progress */}
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-5 gap-4 mb-4">
          {/* Assigned (acts as reset to "all of this employee's drivers") */}
          <button
            type="button"
            onClick={() => handleClick("reset", null)}
            disabled={loading}
            title="Show all drivers assigned to this employee"
            className={`relative ${cardBaseClass} bg-gradient-to-r from-indigo-500 to-indigo-600 ${
              isActive("reset", null) ? activeRingClass : ""
            } ${loading ? "cursor-wait" : "cursor-pointer"}`}
          >
            {isActive("reset", null) && <ActiveBadge />}
            <div className="flex items-center justify-between mb-2">
              <span className="text-xs uppercase tracking-wider opacity-90">
                Assigned
              </span>
              <FaUserTie className="text-lg opacity-80" />
            </div>
            <div className="text-3xl font-bold leading-tight">
              {renderCount(stats.assignedTotal)}
            </div>
            <div
              className="text-xs opacity-90 mt-1 truncate"
              title={assignedEmployeeName}
            >
              {assignedEmployeeName || "—"}
            </div>
          </button>

          {/* Initial Call container — Done / Pending sub-tiles are independently clickable */}
          <div className="relative bg-gradient-to-r from-sky-500 to-sky-600 rounded-xl p-4 shadow-md text-white">
            <div className="flex items-center justify-between mb-2">
              <span className="text-xs uppercase tracking-wider opacity-90">
                Initial Call
              </span>
              <FaPhoneAlt className="text-lg opacity-80" />
            </div>
            <div className="grid grid-cols-2 gap-2">
              <button
                type="button"
                onClick={() => handleClick("initialCallStatus", "done")}
                disabled={loading}
                title="Drivers with at least one call entry"
                className={`relative bg-white bg-opacity-15 rounded-lg px-2 py-1.5 text-left transition hover:bg-opacity-25 focus:outline-none focus:ring-2 focus:ring-white ${
                  isActive("initialCallStatus", "done")
                    ? "ring-2 ring-white"
                    : ""
                } ${loading ? "cursor-wait" : "cursor-pointer"}`}
              >
                {isActive("initialCallStatus", "done") && (
                  <span className="absolute -top-1 -right-1 bg-white text-[9px] font-semibold uppercase tracking-wider text-gray-700 rounded-full px-1.5 py-0.5 shadow-sm">
                    On
                  </span>
                )}
                <div className="text-[10px] uppercase opacity-90">Done</div>
                <div className="text-xl font-bold">
                  {renderCount(stats.callStatus?.done)}
                </div>
              </button>
              <button
                type="button"
                onClick={() => handleClick("initialCallStatus", "pending")}
                disabled={loading}
                title="Drivers with no call entries yet"
                className={`relative bg-white bg-opacity-15 rounded-lg px-2 py-1.5 text-left transition hover:bg-opacity-25 focus:outline-none focus:ring-2 focus:ring-white ${
                  isActive("initialCallStatus", "pending")
                    ? "ring-2 ring-white"
                    : ""
                } ${loading ? "cursor-wait" : "cursor-pointer"}`}
              >
                {isActive("initialCallStatus", "pending") && (
                  <span className="absolute -top-1 -right-1 bg-white text-[9px] font-semibold uppercase tracking-wider text-gray-700 rounded-full px-1.5 py-0.5 shadow-sm">
                    On
                  </span>
                )}
                <div className="text-[10px] uppercase opacity-90">Pending</div>
                <div className="text-xl font-bold">
                  {renderCount(stats.callStatus?.pending)}
                </div>
              </button>
            </div>
          </div>

          {/* 1st Follow-up */}
          <button
            type="button"
            onClick={() => handleClick("callTypeAny", "1st Followup")}
            disabled={loading}
            title="Drivers with a 1st Followup entry in their call history"
            className={`relative ${cardBaseClass} bg-gradient-to-r from-purple-500 to-purple-600 ${
              isActive("callTypeAny", "1st Followup") ? activeRingClass : ""
            } ${loading ? "cursor-wait" : "cursor-pointer"}`}
          >
            {isActive("callTypeAny", "1st Followup") && <ActiveBadge />}
            <div className="flex items-center justify-between mb-2">
              <span className="text-xs uppercase tracking-wider opacity-90">
                1st Follow-up
              </span>
              <FaRedo className="text-lg opacity-80" />
            </div>
            <div className="text-3xl font-bold leading-tight">
              {renderCount(stats.callType?.firstFollowup)}
            </div>
            <div className="text-xs opacity-90 mt-1">Drivers contacted</div>
          </button>

          {/* 2nd Follow-up */}
          <button
            type="button"
            onClick={() => handleClick("callTypeAny", "2nd Followup")}
            disabled={loading}
            title="Drivers with a 2nd Followup entry in their call history"
            className={`relative ${cardBaseClass} bg-gradient-to-r from-fuchsia-500 to-fuchsia-600 ${
              isActive("callTypeAny", "2nd Followup") ? activeRingClass : ""
            } ${loading ? "cursor-wait" : "cursor-pointer"}`}
          >
            {isActive("callTypeAny", "2nd Followup") && <ActiveBadge />}
            <div className="flex items-center justify-between mb-2">
              <span className="text-xs uppercase tracking-wider opacity-90">
                2nd Follow-up
              </span>
              <FaRedo className="text-lg opacity-80" />
            </div>
            <div className="text-3xl font-bold leading-tight">
              {renderCount(stats.callType?.secondFollowup)}
            </div>
            <div className="text-xs opacity-90 mt-1">Drivers contacted</div>
          </button>

          {/* 3rd Follow-up */}
          <button
            type="button"
            onClick={() => handleClick("callTypeAny", "3rd Followup")}
            disabled={loading}
            title="Drivers with a 3rd Followup entry in their call history"
            className={`relative ${cardBaseClass} bg-gradient-to-r from-pink-500 to-pink-600 ${
              isActive("callTypeAny", "3rd Followup") ? activeRingClass : ""
            } ${loading ? "cursor-wait" : "cursor-pointer"}`}
          >
            {isActive("callTypeAny", "3rd Followup") && <ActiveBadge />}
            <div className="flex items-center justify-between mb-2">
              <span className="text-xs uppercase tracking-wider opacity-90">
                3rd Follow-up
              </span>
              <FaRedo className="text-lg opacity-80" />
            </div>
            <div className="text-3xl font-bold leading-tight">
              {renderCount(stats.callType?.thirdFollowup)}
            </div>
            <div className="text-xs opacity-90 mt-1">Drivers contacted</div>
          </button>
        </div>

        {/* Row 2 — Driver status counts */}
        <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-5 gap-4">
          {driverStatusOrder.map((key) => {
            const cfg = driverStatusConfig[key];
            const Icon = cfg.icon;
            const count = stats.driverStatus?.[key] ?? 0;
            const active = isActive("panelStatus", key);
            return (
              <button
                key={key}
                type="button"
                onClick={() => handleClick("panelStatus", key)}
                disabled={loading}
                title={`Drivers with status ${cfg.label}`}
                className={`relative ${cardBaseClass} ${cfg.gradient} ${
                  active ? activeRingClass : ""
                } ${loading ? "cursor-wait" : "cursor-pointer"}`}
              >
                {active && <ActiveBadge />}
                <div className="flex items-center justify-between mb-2">
                  <span className="text-xs uppercase tracking-wider opacity-90">
                    {cfg.label}
                  </span>
                  <Icon className="text-lg opacity-80" />
                </div>
                <div className="text-3xl font-bold leading-tight">
                  {renderCount(count)}
                </div>
                <div className="text-xs opacity-90 mt-1">Driver status</div>
              </button>
            );
          })}
        </div>
      </div>
    </div>
  );
};

export default AssignedEmployeeStatsPanel;
